<template>
  <div
    class="on-off-switch"
    :class="{ transitioning, disabled }"
    @click="onClick"
    :data-test="testId"
  >
    <label class="sr-only" :for="idCalc" v-if="ariaLabel && !ariaLabelledBy">
      {{ ariaLabel }}
    </label>
    <input
      type="checkbox"
      :id="idCalc"
      :checked="isChecked"
      :disabled="disabled"
      :aria-labelledby="ariaLabelledBy ? ariaLabelledBy : null"
    />
    <span class="slider" aria-hidden="true">
      <span class="slide-toggle" />
    </span>
  </div>
</template>

<script>
import { computed, ref, watch } from "vue";
import store from "@/store";
import uniqueId from "lodash-es/uniqueId";

export default {
  name: "Toggle",
  emits: ["change", "input", "update:modelValue"],
  props: {
    testId: {
      type: String,
      default: "toggle-btn",
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    id: [Number, String],
    transitioning: {
      type: Boolean,
      default: false,
    },
    value: {
      type: Boolean,
      default: false,
    },
    target: {
      type: Boolean,
    },
    modelValue: {
      type: Boolean,
      default: false,
    },
    ariaLabel: String,
    ariaLabelledBy: String,
  },
  setup(props, { emit }) {
    const theme = computed(() => store.getters["theme/get"]);

    const calcValue = computed(() =>
      props.transitioning && typeof props.target === "boolean" ? props.target : props.value
    );
    const isChecked = ref(calcValue.value);

    const onClick = () => {
      isChecked.value = !isChecked.value;
      emit("input", isChecked.value);
      emit("change", isChecked.value);
      emit("update:modelValue", isChecked.value);
    };

    watch(
      () => props.value,
      () => {
        isChecked.value = props.value;
      }
    );

    watch(
      () => props.transitioning,
      (newTransitioning) => {
        if (!newTransitioning) {
          isChecked.value = props.value;
        }
      }
    );

    const colorChecked = computed(() => theme.value?.iconColors?.xi);
    const colorDisabled = computed(() => theme.value?.iconColors?.lambda);
    const colorUnchecked = computed(() => theme.value?.iconColors?.nu);
    const idCalc = computed(() => props.id || uniqueId("toggle-"));
    const switchColor = computed(() => theme.value?.iconColors?.eta);

    return {
      calcValue,
      isChecked,
      colorChecked,
      colorDisabled,
      colorUnchecked,
      idCalc,
      switchColor,
      onClick,
    };
  },
};
</script>

<style lang="scss" scoped>
@import "~@/styles/variables";

.on-off-switch {
  &.disabled,
  &.transitioning {
    pointer-events: none;
    input,
    input:checked {
      + .slider {
        -webkit-transition: 1s;
        transition: 1s;
      }
    }
  }
  &.disabled {
    opacity: 0.5;
  }

  &.transitioning {
    .slider {
      > .slide-toggle {
        &:after {
          border-style: solid;
          border-top-width: 2px;
          border-left-width: 2px;
          border-bottom-width: 1px;
          border-right-width: 2px;
          -webkit-animation: spinner 1s linear infinite;
          animation: spinner 1s linear infinite;

          @keyframes spinner {
            to {
              transform: rotate(360deg);
            }
          }
        }
      }
    }
  }

  position: relative;
  display: inline-block;
  width: 40px;
  height: 20px;
  margin: 5px 10px;
  vertical-align: bottom;

  input {
    opacity: 0;
    &:checked {
      + .slider {
        > .slide-toggle {
          -webkit-transform: translateX(22px);
          -ms-transform: translateX(22px);
          transform: translateX(22px);
        }
      }
    }
  }

  .slider {
    position: absolute;
    cursor: pointer;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    -webkit-transition: 0.4s;
    transition: 0.4s;
    border-radius: 93px;
    display: flex;
    align-items: center;
    justify-content: space-around;
    width: 40px;
    z-index: 5;
    > .slide-toggle {
      display: block;
      position: absolute;
      content: "";
      height: 24px;
      width: 24px;
      left: -3px;
      -webkit-transition: 0.4s;
      transition: 0.4s;
      border-radius: 50%;
      &:after {
        box-sizing: inherit;
        content: "";
        position: absolute;
        width: 100%;
        height: 100%;
        border-radius: 100%;
        border-width: 0;
        border-style: solid;
        left: 0;
      }
    }
  }
}
</style>
